import { SsoCompany, SsoProvider } from './types'

/**
 * Extracts the domain from an email address
 * @param email - The email address
 * @returns The domain part of the email, or null if invalid
 */
export const extractDomainFromEmail = (email: string): string | null => {
  if (!email) return null
  const parts = email.split('@')
  return parts.length === 2 ? parts[1].toLowerCase() : null
}

/**
 * Extracts the domain from a website URL
 * @param website - The website URL (can include protocol, www, path, etc.)
 * @returns The normalized domain, or null if invalid
 */
export const extractDomainFromWebsite = (website: string): string | null => {
  if (!website) return null
  try {
    // Remove protocol if present
    let domain = website.replace(/^https?:\/\//, '')
    // Remove www. if present
    domain = domain.replace(/^www\./, '')
    // Remove path if present
    domain = domain.split('/')[0]
    return domain.toLowerCase()
  } catch {
    return null
  }
}

/**
 * Finds an SSO company that matches the given email domain
 * @param emailDomain - The email domain to match
 * @param companies - Array of SSO companies to search
 * @returns The matching SsoCompany, or null if not found
 */
export const findCompanyByEmailDomain = (
  emailDomain: string,
  companies: SsoCompany[]
): SsoCompany | null => {
  if (!emailDomain || !companies || companies.length === 0) return null

  return (
    companies.find((company) => {
      const companyDomain = extractDomainFromWebsite(company.website)
      return companyDomain === emailDomain
    }) || null
  )
}

const SSO_STATE_PROVIDERS = new Set<string>(['okta', 'azure'])

/**
 * Extracts the company ID from an SSO state parameter.
 * State format: {provider}_{companyId}_{timestamp} (e.g. okta_... or azure_...)
 * @param state - The state parameter from the OAuth callback
 * @returns The company ID, or null if not found or invalid format
 */
export const extractCompanyIdFromState = (state: string): string | null => {
  if (!state) return null

  const parts = state.split('_')
  if (parts.length >= 3 && SSO_STATE_PROVIDERS.has(parts[0])) {
    return parts[1]
  }

  return null
}

/**
 * Build the OIDC authorize URL for the given SSO company.
 * Okta: {issuer}/v1/authorize (legacy, unchanged)
 * Azure: {tenant_base}/oauth2/v2.0/authorize from issuer .../{tenant}/v2.0
 */
export const buildSsoAuthorizeUrl = (
  company: SsoCompany,
  redirectUri: string,
  state: string
): string => {
  const scope = 'openid email profile'
  const authorizeUrl = getAuthorizeEndpoint(company)
  return (
    `${authorizeUrl}?client_id=${company.clientId}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code` +
    `&scope=${encodeURIComponent(scope)}` +
    `&state=${state}`
  )
}

/**
 * Match mobile + backend Okta issuer normalization:
 * https://{domain}/oauth2 → https://{domain}/oauth2/default
 */
export const normalizeOktaIssuer = (issuer: string): string => {
  const normalized = issuer.trim().replace(/\/+$/, '')
  if (normalized.endsWith('/oauth2')) {
    return `${normalized}/default`
  }
  return normalized
}

export const getAuthorizeEndpoint = (company: SsoCompany): string => {
  const issuer = (company.issuer || '').replace(/\/+$/, '')
  const provider: SsoProvider = company.provider || 'okta'

  if (!SSO_STATE_PROVIDERS.has(provider)) {
    throw new Error(`Unsupported SSO provider: ${provider}`)
  }

  if (provider === 'azure') {
    const tenantBase = issuer.endsWith('/v2.0')
      ? issuer.slice(0, -'/v2.0'.length)
      : issuer
    return `${tenantBase}/oauth2/v2.0/authorize`
  }

  return `${normalizeOktaIssuer(issuer)}/v1/authorize`
}

export const buildSsoState = (
  provider: SsoProvider | string,
  companyId: string
): string => {
  if (!SSO_STATE_PROVIDERS.has(provider)) {
    throw new Error(`Unsupported SSO provider: ${provider}`)
  }
  return `${provider}_${companyId}_${Date.now()}`
}
